import {
  type AbstractTemplateInOut,
  type AnnulOperation,
  type ApplicationValidationError,
  type ChangesetHistory,
  type ChangesetValidationError,
  type ConfigurationOperation,
  type ConfigurationValidationError,
  type DependencyValidationError,
  isChangesetApplicationValidationError,
  isChangesetDependencyValidationError,
  isTemplateApplicationValidationError,
  isTemplateDependencyExpressionValidationError,
  isTemplateDependencyFunctionValidationError,
  type OperationValidationError,
  type TemplateApplicationValidationError,
  type TemplateDependencyValidationError,
  type TemplateInOut,
  type WorkspaceOperationInOut,
} from '@festina/configuration-general-types/configuration'
import type { LayerKey } from '@festina-life/api'
import type { Except, OmitRecursively, Optional } from '@festina-life/core'

export type WithoutOpenApiType<R extends Record<string, unknown>> =
  OmitRecursively<R, '_type'>

export type TemplateFunctionKeys = 'declaredFunctions' | 'dynamicFunctions'

export type FieldOrigin = string

export type WithOptionalOriginInfo<T> =
  T extends Record<string, unknown>
    ? { [K in keyof T]: WithOptionalOriginInfo<T[K]> } & {
        origin?: Optional<FieldOrigin>
      }
    : T extends (infer A)[]
      ? WithOptionalOriginInfo<A>[]
      : T

export const ENRICHABLE_RAW_KEYS = [
  'valueConfig',
  'documentation',
  'parent',
] as const
type SpecificRawKeys = (typeof ENRICHABLE_RAW_KEYS)[number]

export type VersionedValue<T> = {
  value: NonNullable<T>
  origin: FieldOrigin
}

export type WithEnrichedRawTypes<T> =
  T extends Record<string, unknown>
    ? {
        [K in keyof T]: K extends SpecificRawKeys
          ? T[K] extends null | undefined
            ? never
            : VersionedValue<T[K]>
          : WithEnrichedRawTypes<T[K]>
      }
    : T extends (infer A)[]
      ? WithEnrichedRawTypes<A>[]
      : T

export type Enriched<T> = WithOptionalOriginInfo<WithEnrichedRawTypes<T>>

export const isDependencyValidationError = (
  error: ConfigurationValidationError,
): error is DependencyValidationError =>
  error._type === 'DependencyValidationError' ||
  isTemplateDependencyValidationError(error) ||
  isTemplateDependencyFunctionValidationError(error)

export const isChangesetValidationError = (
  error: ConfigurationValidationError,
): error is ChangesetValidationError =>
  error._type === 'ChangesetValidationError' ||
  isChangesetApplicationValidationError(error) ||
  isChangesetDependencyValidationError(error)

export const isTemplateDependencyValidationError = (
  error: ConfigurationValidationError,
): error is TemplateDependencyValidationError =>
  error._type === 'TemplateDependencyValidationError' ||
  isTemplateDependencyFunctionValidationError(error) ||
  isTemplateDependencyExpressionValidationError(error)

export const isTemplateValidationErrorWithPath = (
  error: ConfigurationValidationError,
): error is
  | TemplateDependencyValidationError
  | TemplateApplicationValidationError =>
  isTemplateDependencyValidationError(error) ||
  isTemplateApplicationValidationError(error)

export const isApplicationValidationError = (
  error: ConfigurationValidationError,
): error is ApplicationValidationError =>
  error._type === 'ApplicationValidationError' ||
  isTemplateApplicationValidationError(error)

export const isOperationValidationError = (
  error: ConfigurationValidationError,
): error is OperationValidationError =>
  error._type === 'OperationValidationError' ||
  isDependencyValidationError(error) ||
  isApplicationValidationError(error)

export const isTemplateInOut = (
  value: AbstractTemplateInOut,
): value is TemplateInOut => value._type === 'TemplateInOut'

export type UserAction = 'create' | 'approve'

export type UserActionData = {
  action: UserAction
  userId: Optional<string>
  actionTime: Optional<string>
}

export const isApproved = (actions: UserActionData[]): boolean =>
  actions.some((action) => action.action === 'approve' && action.actionTime)

export type OperationWithSourceInfo<T extends ConfigurationOperation> = {
  isDraft: boolean
  layer: LayerKey
  block: string
  actions: UserActionData[]
  changesetHistory: Optional<ChangesetHistory>
  annulOperations: OperationWithSourceInfo<AnnulOperation>[]
  internalOperation: T
}

/**
 * Configuration item that indexes the versions of a single entity, such as a template,
 * a parameter definition or a GL-map, together with whether any of them is a draft.
 */
export type IndexedOperations<T extends ConfigurationOperation> = {
  /**
   * Whether any draft operation exists for the entity.
   */
  isDraft: boolean
  /**
   * All the operations of the entity, sorted by their from date, block depth and the
   * order in which they appear in the changeset.
   */
  operations: OperationWithSourceInfo<T>[]
}

/**
 * Workspace (draft) operation holding a specific type of configuration operation.
 */
export type WorkspaceOperationOf<T extends ConfigurationOperation> = Except<
  WorkspaceOperationInOut,
  'operation'
> & {
  operation: T
}

export const ConfigurationPersonas = [
  'ConfigurationEditor',
  'ConfigurationViewer',
  'ConfigurationAdmin',
] as const

export type ConfigurationPersona = (typeof ConfigurationPersonas)[number]

export const isConfigurationPersona = (
  persona: string,
): persona is ConfigurationPersona =>
  ConfigurationPersonas.includes(persona as ConfigurationPersona)

export const canPersonaEditConfiguration = (
  persona: ConfigurationPersona,
): boolean => ['ConfigurationEditor', 'ConfigurationAdmin'].includes(persona)
